import {
	type CustomField,
	type Flag,
	getFlagColor,
	getItemFields,
	ItemStatusFilter,
	type ItemType,
} from '@cheqroom/api';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';

import type { FilterFieldEntry } from './types';
import { getDynamicFilterEntries } from './utils/get-dynamic-filter-entries';

const EMPTY_HIDE_FILTERS: string[] = [];

export const getItemStatusOptions = (t: TFunction<'item-filters'>) => [
	{
		label: t('item-filters:status.options.available'),
		value: ItemStatusFilter.AVAILABLE,
	},
	{
		label: t('item-filters:status.options.unavailable'),
		value: ItemStatusFilter.UNAVAILABLE,
	},
	{
		label: t('item-filters:status.options.checked_out'),
		value: ItemStatusFilter.CHECKED_OUT,
	},
	{
		label: t('item-filters:status.options.checking_out'),
		value: ItemStatusFilter.CHECKING_OUT,
	},
	{
		label: t('item-filters:status.options.in_custody'),
		value: ItemStatusFilter.IN_CUSTODY,
	},
	{
		label: t('item-filters:status.options.retired'),
		value: ItemStatusFilter.RETIRED,
	},
	{
		label: t('item-filters:status.options.in_warranty'),
		value: ItemStatusFilter.IN_WARRANTY,
	},
	{
		label: t('item-filters:status.options.out_of_warranty'),
		value: ItemStatusFilter.OUT_OF_WARRANTY,
	},
	{
		label: t('item-filters:status.options.with_flag'),
		value: ItemStatusFilter.WITH_FLAG,
	},
	{
		label: t('item-filters:status.options.without_flag'),
		value: ItemStatusFilter.WITHOUT_FLAG,
	},
];

export const getItemKindOptions = (t: TFunction<'item-filters'>) => [
	{ label: t('item-filters:kind.options.all'), value: 'all' },
	{ label: t('item-filters:kind.options.simple'), value: 'simple' },
	{ label: t('item-filters:kind.options.bulk'), value: 'bulk' },
];

export const getItemKittedOptions = (t: TFunction<'item-filters'>) => [
	{ label: t('item-filters:kitted.options.all'), value: 'all' },
	{ label: t('item-filters:kitted.options.kitted'), value: 'kitted' },
	{ label: t('item-filters:kitted.options.not_kitted'), value: 'non_kitted' },
];

const getItemFlagOptions = (itemFlags: Flag[]) =>
	itemFlags.map((flag) => ({
		label: flag.name,
		value: flag.id,
		color: getFlagColor(flag.color),
		icon: 'FlagAltFilled',
	}));

const getItemTypeOptions = (itemTypes: ItemType[]) =>
	itemTypes.map((itemType) => ({ label: itemType.name, value: itemType.id }));

interface UseItemFiltersProps {
	t: TFunction<'item-filters'>;
	itemTypeId?: string;
	hasDepreciationPermission?: boolean;
	customFields?: CustomField[];
	currency?: string | null;
	itemFlags?: Flag[];
	itemTypes?: ItemType[];
	hideFilters?: string[];
}

export const useItemFilters = ({
	t,
	itemTypeId,
	hasDepreciationPermission,
	customFields,
	currency,
	itemFlags,
	itemTypes,
	hideFilters = EMPTY_HIDE_FILTERS,
}: UseItemFiltersProps) => {
	const filters = useMemo<FilterFieldEntry[]>(() => {
		const systemFieldsVisibility = itemTypes?.find(
			(itemType) => itemType.id === itemTypeId
		)?.system_fields_visibility;

		const allStaticFilters: FilterFieldEntry[] = [
			{ id: 'status', name: t('filter.status.trigger'), type: 'select', options: getItemStatusOptions(t) },
			{ id: 'categories', name: t('item-filters:categories.trigger') },
			{ id: 'locations', name: t('filter.location.trigger') },
			{
				id: 'flags',
				name: t('item-filters:flags.trigger'),
				type: 'select',
				options: itemFlags ? getItemFlagOptions(itemFlags) : undefined,
			},
			{
				id: 'kind',
				name: t('item-filters:kind.trigger'),
				type: 'select',
				selectMode: 'single',
				options: getItemKindOptions(t),
			},
			{
				id: 'itemTypes',
				name: t('item-filters:item_type.trigger'),
				type: 'select',
				options: itemTypes ? getItemTypeOptions(itemTypes) : undefined,
			},
			{
				id: 'kitted',
				name: t('item-filters:kitted.trigger'),
				type: 'select',
				selectMode: 'single',
				options: getItemKittedOptions(t),
			},
		];
		const staticFilters = allStaticFilters.filter((entry) => !hideFilters.includes(entry.id));

		const itemFields = getItemFields({ systemFieldsVisibility, hasDepreciationPermission, customFields, currency });
		const dynamicFilters = getDynamicFilterEntries(itemFields.filter((field) => !hideFilters.includes(field.id)));

		return [...staticFilters, ...dynamicFilters];
	}, [t, itemTypeId, hasDepreciationPermission, customFields, currency, itemFlags, itemTypes, hideFilters]);

	return {
		filters,
	};
};
